'use client';

import React from 'react';
import Script from 'next/script';
import { useTranslation } from 'react-i18next';
import { SessionContext } from '@benzinga/session-context';
import { ShopManager } from '@benzinga/shop-manager';
import { SubscriptionsManager } from '@benzinga/subscription-manager';
import type { UserSubscription } from '@benzinga/subscription-manager';
import CancelSubscriptionModal from '../../../../src/components/Account/CancelModal';
import { useAccountData } from '../_shell/AccountDataProvider';
import { BillingSupport } from './BillingSupport';
import { PaymentMethods } from './PaymentMethods';
import { SubscriptionsCard } from './SubscriptionsCard';
import { buildSubscriptionGroups } from './subscriptions';
import styles from './Billing.module.css';

export function PlansBillingSection() {
  const { t } = useTranslation(['profile', 'common']);
  const session = React.useContext(SessionContext);
  const subscriptionManager = session.getManager(SubscriptionsManager);
  const shopManager = session.getManager(ShopManager);
  const { cards, isLoadingUser, subscriptions, user } = useAccountData();

  const [subToCancel, setSubToCancel] = React.useState<UserSubscription | null>(null);
  const [isChargebeeLoaded, setIsChargebeeLoaded] = React.useState(false);

  const groups = React.useMemo(() => buildSubscriptionGroups(subscriptions), [subscriptions]);

  return (
    <section>
      <Script
        onReady={() => setIsChargebeeLoaded(true)}
        src="https://js.chargebee.com/v2/chargebee.js"
        strategy="afterInteractive"
      />
      <h2 className="acct-section-title">
        {t('Billing.plans-billing', { defaultValue: 'Plans & Billing', ns: 'profile' })}
      </h2>
      <p className="acct-section-sub">
        {t('Billing.section-sub', {
          defaultValue: 'Your subscriptions and payment methods, all in one place.',
          ns: 'profile',
        })}
      </p>

      <div className={styles.stack}>
        <PaymentMethods
          cards={cards}
          isChargebeeLoaded={isChargebeeLoaded}
          isLoading={isLoadingUser}
          shopManager={shopManager}
        />
        <SubscriptionsCard
          groups={groups}
          isLoading={isLoadingUser}
          setSubToCancel={setSubToCancel}
          subscriptionManager={subscriptionManager}
        />
        <BillingSupport />
      </div>

      {subToCancel && (
        <CancelSubscriptionModal
          creditCards={cards}
          setSubToCancel={setSubToCancel}
          subscription={subToCancel}
          user={user}
        />
      )}
    </section>
  );
}
